import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

export const fetchCarrierLoads = createAsyncThunk(
  'loadCarrier/fetchLoads',
  async ({ page = 1, perPage = 50, sortBy = 'pickup_date', sortOrder = 'asc', status = 'all', search = '' }, { rejectWithValue }) => {
    try {
      const response = await api.get('/api/carrier/loads', {
        params: {
          page,
          per_page: perPage,
          sort_by: sortBy,
          sort_order: sortOrder,
          status,
          search,
          include: ['documents']
        }
      });

      return {
        loads: response.data.loads.map(load => ({
          ...load,
          documents: load.documents || []
        })),
        pagination: {
          page: response.data.page,
          pages: response.data.pages,
          perPage: response.data.per_page,
          total: response.data.total,
          nextPage: response.data.next_page
        }
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred');
    }
  }
);

export const fetchCarrierLoadDocuments = createAsyncThunk(
  'loadCarrier/fetchLoadDocuments',
  async (loadId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/loads/${loadId}/documents`);
      return {
        loadId,
        documents: response.data.documents
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch documents');
    }
  }
);

export const fetchSingleCarrierLoad = createAsyncThunk(
  'loadCarrier/fetchSingleLoad',
  async (loadId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/api/loads/${loadId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) {
        return rejectWithValue({ type: 'unauthorized', message: 'Authentication required' });
      }
      if (error.response?.status === 403) {
        return rejectWithValue({ type: 'forbidden', message: 'Access denied' });
      }
      return rejectWithValue(error.response?.data || 'Failed to fetch load');
    }
  }
);

const initialState = {
  loads: [],
  pagination: {
    page: 1,
    pages: 1,
    perPage: 50,
    total: 0,
    nextPage: null
  },
  status: 'idle',
  error: null,
  activeTab: 'active'
};

const loadCarrierSlice = createSlice({
  name: 'loadCarrier',
  initialState,
  reducers: {
    updateCarrierLoad: (state, action) => {
      // Allow two payload shapes:
      // 1. Full load object
      // 2. { id, data } – legacy callers
      const updatedLoad = action.payload.data ? {
        ...action.payload.data,
        id: action.payload.id || action.payload.data.id
      } : action.payload;
      const index = state.loads.findIndex(load => load.id === updatedLoad.id);
      
      // Process bids to ensure consistent property naming if bids exist
      let processedBids = [];
      if (updatedLoad.bids && updatedLoad.bids.length > 0) {
        processedBids = updatedLoad.bids.map(bid => ({
          ...bid,
          bid_type: bid.bid_type || bid.bidType || 'yes',
          bidType: bid.bid_type || bid.bidType || 'yes',
          alt_pickup_date: bid.alt_pickup_date || bid.altPickupDate,
          altPickupDate: bid.alt_pickup_date || bid.altPickupDate,
          alt_pickup_time: bid.alt_pickup_time || bid.altPickupTime,
          altPickupTime: bid.alt_pickup_time || bid.altPickupTime,
          alt_delivery_date: bid.alt_delivery_date || bid.altDeliveryDate,
          altDeliveryDate: bid.alt_delivery_date || bid.altDeliveryDate,
          alt_delivery_time: bid.alt_delivery_time || bid.altDeliveryTime,
          altDeliveryTime: bid.alt_delivery_time || bid.altDeliveryTime
        }));
      }
      
      if (index !== -1) {
        state.loads[index] = {
          ...state.loads[index],
          ...updatedLoad,
          assigned_driver: updatedLoad.assigned_driver_id !== null,
          status: updatedLoad.status,
          status_history: updatedLoad.status_history || state.loads[index].status_history,
          documents: updatedLoad.documents || state.loads[index].documents || [],
          bids: processedBids.length > 0 ? processedBids : (updatedLoad.bids || state.loads[index].bids || [])
        };
      } else {
        state.loads.push({
          ...updatedLoad,
          assigned_driver: updatedLoad.assigned_driver_id !== null,
          documents: updatedLoad.documents || [],
          bids: processedBids.length > 0 ? processedBids : (updatedLoad.bids || [])
        });
        state.pagination.total += 1;
      }
    },
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    updateLoadStatus: (state, action) => {
      const { loadId, status } = action.payload;
      const loadIndex = state.loads.findIndex(load => load.id === loadId);
      if (loadIndex !== -1) {
        state.loads[loadIndex].status = status;
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCarrierLoads.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCarrierLoads.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.loads = action.payload.loads;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchCarrierLoads.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(fetchCarrierLoadDocuments.fulfilled, (state, action) => {
        const { loadId, documents } = action.payload;
        const loadIndex = state.loads.findIndex(load => load.id === loadId);
        if (loadIndex !== -1) {
          state.loads[loadIndex].documents = documents.load_documents || [];
        }
      })
      .addCase(fetchSingleCarrierLoad.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSingleCarrierLoad.fulfilled, (state, action) => {
        const loadIndex = state.loads.findIndex(load => load.id === action.payload.id);
        
        // Process bids to ensure consistent property naming
        let processedBids = [];
        if (action.payload.bids && action.payload.bids.length > 0) {
          processedBids = action.payload.bids.map(bid => ({
            ...bid,
            bid_type: bid.bid_type || bid.bidType || 'yes',
            bidType: bid.bid_type || bid.bidType || 'yes',
            alt_pickup_date: bid.alt_pickup_date || bid.altPickupDate,
            altPickupDate: bid.alt_pickup_date || bid.altPickupDate,
            alt_pickup_time: bid.alt_pickup_time || bid.altPickupTime,
            altPickupTime: bid.alt_pickup_time || bid.altPickupTime,
            alt_delivery_date: bid.alt_delivery_date || bid.altDeliveryDate,
            altDeliveryDate: bid.alt_delivery_date || bid.altDeliveryDate,
            alt_delivery_time: bid.alt_delivery_time || bid.altDeliveryTime,
            altDeliveryTime: bid.alt_delivery_time || bid.altDeliveryTime
          }));
        }
        
        if (loadIndex !== -1) {
          state.loads[loadIndex] = {
            ...state.loads[loadIndex],
            ...action.payload,
            stops: action.payload.stops || [],
            documents: action.payload.documents || [],
            status_history: action.payload.status_history || [],
            assigned_driver: action.payload.assigned_driver || null,
            bids: processedBids.length > 0 ? processedBids : (action.payload.bids || [])
          };
        } else {
          state.loads.push({
            ...action.payload,
            stops: action.payload.stops || [],
            documents: action.payload.documents || [],
            status_history: action.payload.status_history || [],
            assigned_driver: action.payload.assigned_driver || null,
            bids: processedBids.length > 0 ? processedBids : (action.payload.bids || [])
          });
        }
        state.status = 'succeeded';
      })
      .addCase(fetchSingleCarrierLoad.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { 
  updateCarrierLoad, 
  setActiveTab, 
  updateLoadStatus 
} = loadCarrierSlice.actions;
export default loadCarrierSlice.reducer;

// Add these selectors
export const selectCarrierLoads = (state) => state.loadCarrier.loads;
export const selectCarrierLoadById = (state, loadId) => {
  const loads = state.loadCarrier.loads;
  if (!loads) return null;
  // Support both integer ID and UUID lookups for backwards compatibility
  const idStr = loadId?.toString();
  return loads.find(load => 
    load.id?.toString() === idStr || 
    load.uuid === idStr ||
    load.uuid?.toLowerCase() === idStr?.toLowerCase()
  );
};
export const selectActiveTab = (state) => state.loadCarrier.activeTab;
